import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
	Alert,
	Box,
	Chip,
	CircularProgress,
	Grid,
	IconButton,
	Stack,
	ToggleButton,
	ToggleButtonGroup,
	Tooltip,
	Typography,
	useTheme,
} from '@mui/material';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import AccountBalanceWalletOutlinedIcon from '@mui/icons-material/AccountBalanceWalletOutlined';
import ScheduleOutlinedIcon from '@mui/icons-material/ScheduleOutlined';
import SavingsOutlinedIcon from '@mui/icons-material/SavingsOutlined';
import PercentOutlinedIcon from '@mui/icons-material/PercentOutlined';
import { BarChart } from '@mui/x-charts/BarChart';
import { PieChart } from '@mui/x-charts/PieChart';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import { useSnackbar } from 'notistack';
import {
	ACTION_TYPES,
	useAppContext,
	useFeature,
} from '../../Context/AppContext';
import { fetchAnalyticsDashboard } from '../../Shared/Api';
import { markProOnboardingStepSeen } from '../../Shared/onboarding';
import { surfaceCard } from '../../Shared/surfaceCard';

const PRESETS = [
	{ id: '30', label: '30 days', days: 29 },
	{ id: '90', label: '90 days', days: 89 },
	{ id: 'ytd', label: 'YTD' },
	{ id: 'custom', label: 'Custom' },
];

const AGING_LABELS = {
	'0_30': '0-30 days',
	'31_60': '31-60 days',
	'61_90': '61-90 days',
	'90_plus': '90+ days',
};

const STATUS_COLOR = {
	default: 'default',
	primary: 'primary',
	warning: 'warning',
	success: 'success',
	error: 'error',
	info: 'info',
};

const TIPS = {
	revenue:
		'Money you asked customers to pay (invoiced) vs money that actually came in (paid) during the selected dates.',
	pipeline:
		'How proposals moved in this date range: sent out, still waiting, won, lost, or expired without an answer.',
	cycle_time:
		'Median days once a bid is accepted until the job is marked complete, and until the final payment lands. Lower is faster.',
	labor:
		'Approved crew hours in this range, plus labor cost from each user’s hourly cost rate. Hours without a rate count as $0.',
	outstanding:
		'Unpaid invoice balances right now (not limited to the date picker). Aging shows how long those balances have been open.',
	jobs_by_status:
		'Snapshot of every open job today, grouped by your job status labels. Not filtered by the date range.',
	collection:
		'Paid divided by invoiced in this range. Higher means more of what you billed actually collected.',
	win_rate:
		'Accepted bids divided by decided bids (accepted + declined + expired) created in this range.',
	profit:
		'Cash collected minus job costs recorded and labor cost (hours × hourly rates) in this range.',
	margin:
		'Profit divided by cash collected in this range. Higher means more of every dollar collected is kept after costs.',
	job_costs:
		'Internal job costs (materials, subs, etc.) added in this date range — not shown on customer proposals.',
	quoted:
		'Sum of accepted proposal amounts created in this range. Compare to total costs to see spend against won work.',
};

const formatMoney = (value) => {
	const n = Number(value);
	if (Number.isNaN(n)) return '-';
	return new Intl.NumberFormat('en-US', {
		style: 'currency',
		currency: 'USD',
		maximumFractionDigits: 0,
	}).format(n);
};

const formatMoneyPrecise = (value) => {
	const n = Number(value);
	if (Number.isNaN(n)) return '-';
	return new Intl.NumberFormat('en-US', {
		style: 'currency',
		currency: 'USD',
	}).format(n);
};

const formatPercent = (value) => {
	if (value == null || Number.isNaN(value)) return '-';
	return `${Math.round(value * 100)}%`;
};

const InfoTip = ({ text }) => (
	<Tooltip title={text} arrow enterTouchDelay={0}>
		<IconButton
			size="small"
			aria-label="What does this mean?"
			sx={{ ml: 0.25, p: 0.25 }}
		>
			<HelpOutlineIcon sx={{ fontSize: 18 }} color="action" />
		</IconButton>
	</Tooltip>
);

const MetricCard = ({ title, tip, subtitle, children, dense }) => {
	const theme = useTheme();
	return (
		<Box
			sx={{
				...surfaceCard(theme),
				p: { xs: 2, md: dense ? 2 : 2.5 },
				height: '100%',
				display: 'flex',
				flexDirection: 'column',
			}}
		>
			<Stack direction="row" alignItems="center" spacing={0.5} sx={{ mb: 0.5 }}>
				<Typography variant="subtitle1" fontWeight={700}>
					{title}
				</Typography>
				{tip ? <InfoTip text={tip} /> : null}
			</Stack>
			{subtitle ? (
				<Typography
					variant="caption"
					color="text.secondary"
					sx={{ mb: 1.5, display: 'block' }}
				>
					{subtitle}
				</Typography>
			) : null}
			<Box sx={{ flex: 1 }}>{children}</Box>
		</Box>
	);
};

const StatRow = ({ label, value, tip, emphasize }) => (
	<Stack
		direction="row"
		justifyContent="space-between"
		alignItems="baseline"
		spacing={2}
		sx={{ py: 0.5 }}
	>
		<Stack direction="row" alignItems="center" spacing={0.25} sx={{ minWidth: 0 }}>
			<Typography variant="body2" color="text.secondary" noWrap>
				{label}
			</Typography>
			{tip ? <InfoTip text={tip} /> : null}
		</Stack>
		<Typography
			variant={emphasize ? 'h6' : 'body1'}
			fontWeight={emphasize ? 700 : 600}
			sx={{ fontVariantNumeric: 'tabular-nums', flexShrink: 0 }}
		>
			{value}
		</Typography>
	</Stack>
);

const KpiTile = ({ icon: Icon, label, value, tip, hint }) => {
	const theme = useTheme();
	return (
		<Box
			sx={{
				...surfaceCard(theme),
				p: { xs: 2, md: 2.25 },
				height: '100%',
				background: `linear-gradient(160deg, ${theme.palette.background.paper} 0%, ${theme.palette.action.hover} 140%)`,
			}}
		>
			<Stack spacing={1.25}>
				<Stack direction="row" alignItems="center" justifyContent="space-between">
					<Stack direction="row" spacing={1} alignItems="center">
						<Box
							sx={{
								width: 34,
								height: 34,
								borderRadius: 2,
								display: 'grid',
								placeItems: 'center',
								bgcolor: 'action.selected',
								color: 'primary.main',
							}}
						>
							<Icon sx={{ fontSize: 18 }} />
						</Box>
						<Typography variant="body2" color="text.secondary" fontWeight={600}>
							{label}
						</Typography>
						{tip ? <InfoTip text={tip} /> : null}
					</Stack>
				</Stack>
				<Typography
					variant="h4"
					fontWeight={800}
					sx={{ fontVariantNumeric: 'tabular-nums', letterSpacing: '-0.02em' }}
				>
					{value}
				</Typography>
				{hint ? (
					<Typography variant="caption" color="text.secondary">
						{hint}
					</Typography>
				) : null}
			</Stack>
		</Box>
	);
};

const SectionLabel = ({ children }) => (
	<Typography
		variant="overline"
		color="text.secondary"
		sx={{ letterSpacing: 1.1, fontWeight: 700 }}
	>
		{children}
	</Typography>
);

const AnalyticsPage = () => {
	const theme = useTheme();
	const { state, dispatch } = useAppContext();
	const { enqueueSnackbar } = useSnackbar();
	const hasAnalytics = useFeature('analytics');
	const isManager =
		state.user.role === 'Manager' || Boolean(state.user.isSuperuser);

	const [preset, setPreset] = useState('30');
	const [customFrom, setCustomFrom] = useState(() =>
		dayjs().subtract(29, 'day'),
	);
	const [customTo, setCustomTo] = useState(() => dayjs());
	const today = useMemo(() => dayjs().endOf('day'), []);
	const [data, setData] = useState(null);
	const [loading, setLoading] = useState(true);

	useEffect(() => {
		dispatch({
			type: ACTION_TYPES.SET_PAGE_TITLE,
			payload: { pageTitle: 'Analytics' },
		});
		return () => {
			dispatch({
				type: ACTION_TYPES.SET_PAGE_TITLE,
				payload: { pageTitle: '' },
			});
		};
	}, [dispatch]);

	useEffect(() => {
		if (isManager && hasAnalytics) {
			markProOnboardingStepSeen('analytics');
		}
	}, [isManager, hasAnalytics]);

	const range = useMemo(() => {
		const today = dayjs();
		if (preset === 'custom') {
			return {
				from: customFrom.startOf('day').format('YYYY-MM-DD'),
				to: customTo.endOf('day').format('YYYY-MM-DD'),
			};
		}
		if (preset === 'ytd') {
			return {
				from: today.startOf('year').format('YYYY-MM-DD'),
				to: today.format('YYYY-MM-DD'),
			};
		}
		const days = preset === '90' ? 89 : 29;
		return {
			from: today.subtract(days, 'day').format('YYYY-MM-DD'),
			to: today.format('YYYY-MM-DD'),
		};
	}, [preset, customFrom, customTo]);

	const loadDashboard = useCallback(async () => {
		if (!hasAnalytics) {
			setData(null);
			setLoading(false);
			return;
		}
		setLoading(true);
		try {
			const payload = await fetchAnalyticsDashboard(range);
			setData(payload);
		} catch (error) {
			enqueueSnackbar(error.message || 'Failed to load analytics.', {
				variant: 'error',
			});
			setData(null);
		} finally {
			setLoading(false);
		}
	}, [enqueueSnackbar, hasAnalytics, range]);

	useEffect(() => {
		loadDashboard();
	}, [loadDashboard]);

	const chartPalette = useMemo(
		() => [
			theme.palette.primary.main,
			theme.palette.success.main,
			theme.palette.warning.main,
			theme.palette.error.main,
			theme.palette.info.main,
			theme.palette.secondary?.main || theme.palette.grey[600],
		],
		[theme],
	);

	const kpis = useMemo(() => {
		if (!data) return null;
		const invoiced = Number(data.revenue?.invoiced ?? 0);
		const paid = Number(data.revenue?.paid ?? 0);
		const accepted = Number(data.pipeline?.accepted ?? 0);
		const declined = Number(data.pipeline?.declined ?? 0);
		const expired = Number(data.pipeline?.expired ?? 0);
		const decided = accepted + declined + expired;
		const profit = Number(data.profit?.profit ?? 0);
		const marginPct =
			data.profit?.margin_pct != null ? Number(data.profit.margin_pct) : null;
		return {
			paid,
			invoiced,
			collection: invoiced > 0 ? paid / invoiced : null,
			winRate: decided > 0 ? accepted / decided : null,
			outstanding: Number(data.outstanding?.total_balance ?? 0),
			openInvoices: data.outstanding?.invoice_count ?? 0,
			cycleComplete: data.cycle_time?.approved_to_completed_days,
			pending: data.pipeline?.pending ?? 0,
			profit,
			marginPct,
			jobCosts: Number(data.profit?.job_costs ?? 0),
			laborCost: Number(data.profit?.labor_cost ?? 0),
			totalCosts: Number(data.profit?.total_costs ?? 0),
			quoted: Number(data.profit?.quoted_contract_value ?? 0),
			costVsQuote:
				data.profit?.cost_vs_quote_pct != null
					? Number(data.profit.cost_vs_quote_pct)
					: null,
			hoursMissingRate: Number(data.profit?.hours_missing_rate ?? 0),
		};
	}, [data]);

	if (!hasAnalytics) {
		return (
			<Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 720 }}>
				<Alert severity="info">
					Analytics is available on the Pro plan. Upgrade in Settings -
					Billing to view revenue, pipeline, and labor metrics.
				</Alert>
			</Box>
		);
	}

	const pipelineSeries = data
		? [
				{ label: 'Sent', value: data.pipeline?.sent ?? 0 },
				{ label: 'Pending', value: data.pipeline?.pending ?? 0 },
				{ label: 'Accepted', value: data.pipeline?.accepted ?? 0 },
				{ label: 'Declined', value: data.pipeline?.declined ?? 0 },
				{ label: 'Expired', value: data.pipeline?.expired ?? 0 },
			]
		: [];

	const agingSeries = data
		? Object.entries(AGING_LABELS).map(([key, label]) => ({
				label,
				value: Number(data.outstanding?.aging?.[key]?.balance ?? 0),
				count: data.outstanding?.aging?.[key]?.count ?? 0,
			}))
		: [];

	const jobsSeries = data
		? (data.jobs_by_status?.statuses || []).map((item) => ({
				label: item.label,
				value: item.count ?? 0,
			}))
		: [];

	return (
		<LocalizationProvider dateAdapter={AdapterDayjs}>
			<Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1200, mx: 'auto' }}>
				<Stack spacing={3}>
					<Box
						sx={{
							...surfaceCard(theme),
							p: { xs: 2, md: 2.5 },
						}}
					>
						<Stack
							direction={{ xs: 'column', md: 'row' }}
							spacing={2}
							alignItems={{ md: 'center' }}
							justifyContent="space-between"
						>
							<Box>
								<Typography variant="h6" fontWeight={800} sx={{ mb: 0.25 }}>
									Shop pulse
								</Typography>
								<Typography variant="body2" color="text.secondary">
									{dayjs(range.from).format('MMM D, YYYY')} –{' '}
									{dayjs(range.to).format('MMM D, YYYY')}
								</Typography>
							</Box>
							<ToggleButtonGroup
								exclusive
								size="small"
								value={preset}
								onChange={(_, value) => {
									if (value) setPreset(value);
								}}
								sx={{
									flexWrap: 'wrap',
									'& .MuiToggleButton-root': {
										px: 1.5,
										textTransform: 'none',
										fontWeight: 600,
									},
								}}
							>
								{PRESETS.map((item) => (
									<ToggleButton key={item.id} value={item.id}>
										{item.label}
									</ToggleButton>
								))}
							</ToggleButtonGroup>
						</Stack>

						{preset === 'custom' ? (
							<Stack
								direction={{ xs: 'column', sm: 'row' }}
								spacing={2}
								sx={{ mt: 2 }}
							>
								<DatePicker
									label="From"
									value={customFrom}
									onChange={(value) => value && setCustomFrom(value)}
									maxDate={customTo}
									slotProps={{ textField: { size: 'small', fullWidth: true } }}
								/>
								<DatePicker
									label="To"
									value={customTo}
									onChange={(value) => value && setCustomTo(value)}
									minDate={customFrom}
									maxDate={today}
									slotProps={{ textField: { size: 'small', fullWidth: true } }}
								/>
							</Stack>
						) : null}
					</Box>

					{loading ? (
						<Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
							<CircularProgress />
						</Box>
					) : !data || !kpis ? (
						<Alert severity="warning">No analytics data available.</Alert>
					) : (
						<>
							<Stack spacing={1}>
								<SectionLabel>At a glance</SectionLabel>
								<Grid container spacing={2}>
									<Grid size={{ xs: 12, sm: 6, md: 3 }}>
										<KpiTile
											icon={AccountBalanceWalletOutlinedIcon}
											label="Collected"
											value={formatMoney(kpis.paid)}
											tip={TIPS.revenue}
											hint={`of ${formatMoney(kpis.invoiced)} invoiced`}
										/>
									</Grid>
									<Grid size={{ xs: 12, sm: 6, md: 3 }}>
										<KpiTile
											icon={SavingsOutlinedIcon}
											label="Profit"
											value={formatMoney(kpis.profit)}
											tip={TIPS.profit}
											hint={`${formatMoney(kpis.totalCosts)} in costs`}
										/>
									</Grid>
									<Grid size={{ xs: 12, sm: 6, md: 3 }}>
										<KpiTile
											icon={PercentOutlinedIcon}
											label="Margin"
											value={formatPercent(kpis.marginPct)}
											tip={TIPS.margin}
											hint="Profit ÷ collected"
										/>
									</Grid>
									<Grid size={{ xs: 12, sm: 6, md: 3 }}>
										<KpiTile
											icon={ScheduleOutlinedIcon}
											label="Outstanding"
											value={formatMoney(kpis.outstanding)}
											tip={TIPS.outstanding}
											hint={`${kpis.openInvoices} open invoice${kpis.openInvoices === 1 ? '' : 's'}`}
										/>
									</Grid>
								</Grid>
							</Stack>

							<Stack spacing={1}>
								<SectionLabel>Profit & margin</SectionLabel>
								<Grid container spacing={2}>
									<Grid size={{ xs: 12, md: 6 }}>
										<MetricCard
											title="Period P&L"
											tip={TIPS.profit}
											subtitle="Cash collected vs costs in range"
										>
											<BarChart
												height={220}
												borderRadius={6}
												xAxis={[
													{
														data: ['Collected', 'Costs', 'Profit'],
														scaleType: 'band',
													},
												]}
												series={[
													{
														data: [kpis.paid, kpis.totalCosts, kpis.profit],
														color: theme.palette.success.main,
														valueFormatter: (v) => formatMoney(v),
													},
												]}
												margin={{ left: 60, right: 12, top: 16, bottom: 30 }}
											/>
											<StatRow
												label="Collected"
												tip="Deposits and payments received in this period."
												value={formatMoneyPrecise(data.profit?.revenue_paid)}
												emphasize
											/>
											<StatRow
												label="Job costs"
												tip={TIPS.job_costs}
												value={formatMoneyPrecise(data.profit?.job_costs)}
											/>
											<StatRow
												label="Labor cost"
												tip="Approved hours × each user’s hourly cost. Hours without a rate count as $0."
												value={formatMoneyPrecise(data.profit?.labor_cost)}
											/>
											{kpis.hoursMissingRate > 0 ? (
												<StatRow
													label="Hours missing rate"
													tip="Approved hours from users with no hourly cost set. Labor cost is understated by this much time."
													value={`${kpis.hoursMissingRate.toFixed(1)} hrs`}
												/>
											) : null}
											<StatRow
												label="Total costs"
												tip="Job costs plus labor cost."
												value={formatMoneyPrecise(data.profit?.total_costs)}
											/>
											<StatRow
												label="Profit"
												tip={TIPS.profit}
												value={formatMoneyPrecise(data.profit?.profit)}
												emphasize
											/>
											<StatRow
												label="Margin"
												tip={TIPS.margin}
												value={formatPercent(kpis.marginPct)}
												emphasize
											/>
										</MetricCard>
									</Grid>

									<Grid size={{ xs: 12, md: 6 }}>
										<MetricCard
											title="Quoted vs costs"
											tip={TIPS.quoted}
											subtitle="Won contract value vs spend in range"
										>
											<BarChart
												height={220}
												borderRadius={6}
												xAxis={[
													{
														data: ['Quoted', 'Total costs'],
														scaleType: 'band',
													},
												]}
												series={[
													{
														data: [kpis.quoted, kpis.totalCosts],
														color: theme.palette.info.main,
														valueFormatter: (v) => formatMoney(v),
													},
												]}
												margin={{ left: 60, right: 12, top: 16, bottom: 30 }}
											/>
											<StatRow
												label="Quoted contract value"
												tip={TIPS.quoted}
												value={formatMoneyPrecise(
													data.profit?.quoted_contract_value,
												)}
												emphasize
											/>
											<StatRow
												label="Total costs"
												tip="Job costs plus labor cost recorded in this range."
												value={formatMoneyPrecise(data.profit?.total_costs)}
											/>
											<StatRow
												label="Costs vs quote"
												tip="Total costs divided by quoted contract value. Lower means more headroom vs won work."
												value={formatPercent(kpis.costVsQuote)}
												emphasize
											/>
											<StatRow
												label="Collection rate"
												tip={TIPS.collection}
												value={formatPercent(kpis.collection)}
											/>
											<StatRow
												label="Win rate"
												tip={TIPS.win_rate}
												value={formatPercent(kpis.winRate)}
											/>
										</MetricCard>
									</Grid>
								</Grid>
							</Stack>

							<Stack spacing={1}>
								<SectionLabel>Money & pipeline</SectionLabel>
								<Grid container spacing={2}>
									<Grid size={{ xs: 12, md: 6 }}>
										<MetricCard
											title="Revenue"
											tip={TIPS.revenue}
											subtitle="Invoiced vs paid in range"
										>
											<BarChart
												height={220}
												borderRadius={6}
												xAxis={[
													{
														data: ['Invoiced', 'Paid'],
														scaleType: 'band',
													},
												]}
												series={[
													{
														data: [kpis.invoiced, kpis.paid],
														color: theme.palette.primary.main,
														valueFormatter: (v) => formatMoney(v),
													},
												]}
												margin={{ left: 60, right: 12, top: 16, bottom: 30 }}
											/>
											<StatRow
												label="Invoiced"
												tip="Total invoice amounts emailed or issued in this period."
												value={formatMoney(data.revenue?.invoiced)}
												emphasize
											/>
											<StatRow
												label="Paid"
												tip="Deposits and final payments recorded as received in this period."
												value={formatMoney(data.revenue?.paid)}
												emphasize
											/>
										</MetricCard>
									</Grid>

									<Grid size={{ xs: 12, md: 6 }}>
										<MetricCard
											title="Bid pipeline"
											tip={TIPS.pipeline}
											subtitle="Proposals created in range"
										>
											<BarChart
												height={220}
												borderRadius={6}
												layout="horizontal"
												yAxis={[
													{
														data: pipelineSeries.map((d) => d.label),
														scaleType: 'band',
														width: 72,
													},
												]}
												series={[
													{
														data: pipelineSeries.map((d) => d.value),
														color: theme.palette.info.main,
													},
												]}
												margin={{ left: 0, right: 16, top: 8, bottom: 8 }}
											/>
											{pipelineSeries.map((row) => (
												<StatRow
													key={row.label}
													label={row.label}
													value={row.value}
												/>
											))}
										</MetricCard>
									</Grid>
								</Grid>
							</Stack>

							<Stack spacing={1}>
								<SectionLabel>Speed & labor</SectionLabel>
								<Grid container spacing={2}>
									<Grid size={{ xs: 12, md: 6 }}>
										<MetricCard
											title="Cycle time"
											tip={TIPS.cycle_time}
											subtitle="Median days from bid acceptance"
										>
											<BarChart
												height={200}
												borderRadius={6}
												xAxis={[
													{
														data: ['To complete', 'To paid'],
														scaleType: 'band',
													},
												]}
												series={[
													{
														data: [
															Number(
																data.cycle_time?.approved_to_completed_days ??
																	0,
															),
															Number(
																data.cycle_time?.approved_to_paid_days ?? 0,
															),
														],
														color: theme.palette.warning.main,
														valueFormatter: (v) => `${v} days`,
													},
												]}
												margin={{ left: 40, right: 12, top: 16, bottom: 30 }}
											/>
											<StatRow
												label="Accepted to completed"
												tip="Halfway point (median) for how many days after acceptance until the job is marked complete."
												value={
													data.cycle_time?.approved_to_completed_days != null
														? `${data.cycle_time.approved_to_completed_days} days`
														: '-'
												}
												emphasize
											/>
											<StatRow
												label="Accepted to paid"
												tip="Halfway point (median) for how many days after acceptance until final payment is recorded."
												value={
													data.cycle_time?.approved_to_paid_days != null
														? `${data.cycle_time.approved_to_paid_days} days`
														: '-'
												}
												emphasize
											/>
										</MetricCard>
									</Grid>

									<Grid size={{ xs: 12, md: 6 }}>
										<MetricCard
											title="Labor"
											tip={TIPS.labor}
											subtitle="Approved hours, labor cost, efficiency"
										>
											<BarChart
												height={200}
												borderRadius={6}
												xAxis={[
													{
														data: ['Hours', 'Jobs done'],
														scaleType: 'band',
													},
												]}
												series={[
													{
														data: [
															Number(data.labor?.approved_hours ?? 0),
															Number(data.labor?.jobs_completed ?? 0),
														],
														color: theme.palette.success.main,
													},
												]}
												margin={{ left: 40, right: 12, top: 16, bottom: 30 }}
											/>
											<StatRow
												label="Approved hours"
												tip="Sum of manager-approved clock time in this date range."
												value={`${Number(data.labor?.approved_hours ?? 0).toFixed(1)} hrs`}
												emphasize
											/>
											<StatRow
												label="Labor cost"
												tip="Approved hours × each user’s hourly cost. Hours without a rate count as $0."
												value={formatMoneyPrecise(data.labor?.labor_cost)}
												emphasize
											/>
											{Number(data.labor?.hours_missing_rate ?? 0) > 0 ? (
												<StatRow
													label="Hours missing rate"
													tip="Approved hours from users with no hourly cost set."
													value={`${Number(data.labor.hours_missing_rate).toFixed(1)} hrs`}
												/>
											) : null}
											<StatRow
												label="Jobs completed"
												tip="Jobs marked complete whose completion date falls in this range."
												value={data.labor?.jobs_completed ?? 0}
											/>
											<StatRow
												label="Hours / completed job"
												tip="Approved hours divided by completed jobs. Rough labor load per finished job."
												value={
													data.labor?.hours_per_completed_job != null
														? data.labor.hours_per_completed_job
														: '-'
												}
											/>
											<StatRow
												label="Revenue / hour"
												tip="Paid amount in range divided by approved hours. Rough return on crew time."
												value={
													data.labor?.revenue_per_hour != null
														? formatMoneyPrecise(data.labor.revenue_per_hour)
														: '-'
												}
											/>
										</MetricCard>
									</Grid>
								</Grid>
							</Stack>

							<Stack spacing={1}>
								<SectionLabel>Right now</SectionLabel>
								<Grid container spacing={2}>
									<Grid size={{ xs: 12, md: 6 }}>
										<MetricCard
											title="Outstanding"
											tip={TIPS.outstanding}
											subtitle="Unpaid invoices (current snapshot)"
										>
											{agingSeries.some((d) => d.value > 0) ? (
												<PieChart
													height={220}
													series={[
														{
															data: agingSeries
																.filter((d) => d.value > 0)
																.map((d, index) => ({
																	id: index,
																	value: d.value,
																	label: d.label,
																	color:
																		chartPalette[index % chartPalette.length],
																})),
															innerRadius: 45,
															outerRadius: 85,
															paddingAngle: 2,
															cornerRadius: 4,
															valueFormatter: (item) =>
																formatMoneyPrecise(item.value),
														},
													]}
												/>
											) : (
												<Typography
													variant="body2"
													color="text.secondary"
													sx={{ py: 4, textAlign: 'center' }}
												>
													No open balances.
												</Typography>
											)}
											<StatRow
												label="Open invoices"
												tip="Number of invoices still carrying an unpaid balance."
												value={data.outstanding?.invoice_count ?? 0}
											/>
											<StatRow
												label="Total balance"
												tip="Sum of all unpaid invoice balances today."
												value={formatMoneyPrecise(
													data.outstanding?.total_balance,
												)}
												emphasize
											/>
											<Typography
												variant="caption"
												color="text.secondary"
												sx={{ mt: 1.5, mb: 0.5, display: 'block' }}
											>
												Aging by days overdue
											</Typography>
											{agingSeries.map((row) => (
												<StatRow
													key={row.label}
													label={row.label}
													tip={`Invoices open ${row.label.toLowerCase()}: count and balance still owed.`}
													value={`${row.count} / ${formatMoneyPrecise(row.value)}`}
												/>
											))}
										</MetricCard>
									</Grid>

									<Grid size={{ xs: 12, md: 6 }}>
										<MetricCard
											title="Jobs by status"
											tip={TIPS.jobs_by_status}
											subtitle="All current jobs"
										>
											{jobsSeries.length ? (
												<BarChart
													height={220}
													borderRadius={6}
													layout="horizontal"
													yAxis={[
														{
															data: jobsSeries.map((d) => d.label),
															scaleType: 'band',
															width: 90,
														},
													]}
													series={[
														{
															data: jobsSeries.map((d) => d.value),
															color: theme.palette.primary.main,
														},
													]}
													margin={{ left: 0, right: 16, top: 8, bottom: 8 }}
												/>
											) : null}
											<Stack spacing={1} sx={{ mt: 0.5 }}>
												{(data.jobs_by_status?.statuses || []).map((item) => (
													<Stack
														key={item.slug || 'unassigned'}
														direction="row"
														justifyContent="space-between"
														alignItems="center"
													>
														<Chip
															size="small"
															label={item.label}
															color={STATUS_COLOR[item.color] || 'default'}
															variant="outlined"
														/>
														<Typography fontWeight={700}>{item.count}</Typography>
													</Stack>
												))}
												<StatRow
													label="Total"
													value={data.jobs_by_status?.total ?? 0}
													emphasize
												/>
											</Stack>
										</MetricCard>
									</Grid>
								</Grid>
							</Stack>
						</>
					)}
				</Stack>
			</Box>
		</LocalizationProvider>
	);
};

export default AnalyticsPage;
